import { Info } from 'lucide-react'
import React, { useState } from 'react'

import { useSendReferralStore } from 'src/features/business/referrals/stores/send-referral-store'
import { sendEvent } from 'src/shared/api/events/send-event'
import { Button } from 'src/shared/components/ui/buttons/button'
import { FixedFooter } from 'src/shared/components/ui/miscellaneous/fixed-footer'
import { Separator } from 'src/shared/components/ui/miscellaneous/separator'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { referralFields } from 'src/shared/schemas/referrals/referral-field-definitions'
import { useEventStore } from 'src/shared/store/event-store'
import { cn } from 'src/shared/utils/cn'
import { formatFieldValue } from 'src/shared/utils/format-field-value'

interface ConfirmInfoProps {
  onSendReferral: () => void
}

export const ConfirmInfo: React.FC<ConfirmInfoProps> = ({ onSendReferral }) => {
  const referralFormData = useSendReferralStore((state) => state.referralFormData)
  const handleBack = useSendReferralStore((state) => state.handleBack)
  const { data: currentUser } = useMyUserData()
  const eventStore = useEventStore()
  const sessionId = eventStore.sessionId
  const flowId = eventStore.flowId

  const [isLoading, setIsLoading] = useState(false)

  const handleConfirmAndSend = async () => {
    if (isLoading) return
    setIsLoading(true)

    try {
      await onSendReferral()
      if (currentUser) {
        sendEvent({
          eventType: 'flow_step',
          info: {
            sessionId,
            flowType: 'send_referral',
            flowStep: 'confirm_and_send',
            flowId,
          },
        })
      }
    } catch (error) {
      sendEvent({
        eventType: 'error',
        info: {
          error: error instanceof Error ? error.message : 'An unknown error occurred',
        },
      })
      console.error('Error sending referral')
    } finally {
      setIsLoading(false)
    }
  }

  // Get only the fields that have data
  const filledFields = Object.entries(referralFormData)
    .filter(([_, value]) => {
      if (value === undefined || value === null) return false
      if (Array.isArray(value) && value.length === 0) return false
      if (value === '') return false
      if (typeof value === 'number' && value === 0) return false
      return true
    })
    .map(([key]) => key)

  return (
    <div className="mb-24 space-y-4">
      <div className="space-y-4">
        {filledFields.map((fieldValue) => {
          const fieldValueData = referralFormData[fieldValue as keyof typeof referralFormData]
          const field = referralFields[fieldValue as keyof typeof referralFields]
          if (!field) return null

          const fieldType = field.type
          const isFullWidth = ['text', 'list', 'priceRange'].includes(fieldType)

          return (
            <div key={fieldValue}>
              <div className={cn('flex flex-wrap items-center justify-between gap-2 py-2')}>
                <div className="flex flex-row items-center justify-between">
                  {React.createElement(field.icon, { className: 'mr-3 text-muted' })}
                  <div className="flex-1 font-medium text-primary">
                    {field.label}
                    {field.requiresApproval && (
                      <Info className="ml-2 inline-block size-4 text-muted" aria-label="Requires approval" />
                    )}
                  </div>
                </div>
                <div className={cn('text-muted', !isFullWidth && 'ml-auto')}>
                  {formatFieldValue(field, fieldValueData, referralFormData)}
                </div>
              </div>
              <Separator />
            </div>
          )
        })}
      </div>

      <FixedFooter className="fixed inset-x-0">
        <div className="flex w-full gap-3">
          <Button variant="link" className="w-full" onClick={handleBack}>
            Edit Information
          </Button>
          <Button className="w-full" onClick={handleConfirmAndSend} isLoading={isLoading}>
            Confirm & Send
          </Button>
        </div>
      </FixedFooter>
    </div>
  )
}
